"use client"

import { addToCartWithCustomization, updateCartItemCustomization } from "@lib/data/cart"
import { useIntersection } from "@lib/hooks/use-in-view"
import { useTimedState } from "@lib/hooks/use-timed-state"
import { HttpTypes } from "@medusajs/types"
import OptionSelect from "@modules/products/components/product-actions/option-select"
import { Dialog } from "@headlessui/react"
import { isEqual } from "lodash"
import { CircleCheck, Loader2 } from "lucide-react"
import dynamic from "next/dynamic"
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import ProductPrice from "../product-price"
import MobileActions from "./mobile-actions"
import ShippingMethodWidget from "./shipping-method-widget"

const PaypalExpressButton = dynamic(
  () => import("@modules/products/components/paypal-express-button"),
  {
    ssr: false,
    loading: () => (
      <div className="w-full h-12 rounded-lg bg-[#ffc439]/30 animate-pulse" />
    ),
  }
)
// This page renders PaypalExpressButton twice (inline + floating bar) — both
// must share a single script provider, so it's imported separately and
// wrapped once around both usages. See paypal-express-button/index.tsx.
const PaypalScriptProviderWrapper = dynamic(
  () =>
    import("@modules/products/components/paypal-express-button").then(
      (mod) => mod.PaypalScriptProviderWrapper
    ),
  { ssr: false }
)
import {
  PRODUCT_FEATURE_HIGHLIGHTS,
  CUSTOMIZATION_PRICING,
  CUSTOMIZATION_PATCHES,
  CUSTOMIZATION_LIMITS,
  PERSONALIZATION_PROMPT_LABELS,
} from "@constants/product-page"
import { getProductPrice } from "@lib/util/get-product-price"
import { convertToLocale } from "@lib/util/money"
import { isVariantInStock } from "@lib/util/product"
import Modal from "@modules/common/components/modal"
import ProductCustomization from "@modules/products/components/product-customization"
import ProductThumbnail from "@modules/common/components/product-thumbnail"
import WishlistButton from "@modules/common/components/wishlist-button"
import X from "@modules/common/icons/x"
import NotifyMeButton from "./notify-me-button"
import PaymentIcons from "@modules/products/components/payment-icons"

type ProductActionsProps = {
  product: HttpTypes.StoreProduct
  disabled?: boolean
  paypalEnabled?: boolean
  customer?: HttpTypes.StoreCustomer | null
}

const CATEGORIES = ["Mens", "Womens", "Kids", "Little Kids", "Baby"] as const
type Category = (typeof CATEGORIES)[number]

const CATEGORY_SIZE_KEYWORDS: Record<Category, string[]> = {
  Mens:         ["Adults", "Chest"],
  Womens:       ["Adults", "Chest", "Ladies"],
  Kids:         ["Large Boys", "XL Boys", "Junior", "Youth"],
  "Little Kids":["Small Boys", "Medium Boys", "Mini", "Little Boys"],
  Baby:         ["Baby", "Infant", "Toddler"],
}

type PersonalizationModalFieldProps = {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  maxLength: number
  numeric?: boolean
  testId: string
}

function PersonalizationModalField({
  id,
  label,
  value,
  onChange,
  maxLength,
  numeric = false,
  testId,
}: PersonalizationModalFieldProps) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-brand-navy mb-1.5">
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => {
          const raw = e.target.value.slice(0, maxLength)
          onChange(numeric ? raw.replace(/[^0-9]/g, "") : raw)
        }}
        maxLength={maxLength}
        data-testid={testId}
        className="w-full px-4 py-3 text-sm font-medium text-brand-navy border border-gray-300 rounded-full outline-none focus:border-brand-navy transition-colors"
      />
    </div>
  )
}

function PriceBreakdownRow({ label, value, bold = false }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className={bold ? "text-lg font-bold text-gray-900" : "text-base text-gray-700 italic"}>
        {label}
      </span>
      <span className={bold ? "text-lg font-bold text-brand-navy" : "text-base font-semibold text-gray-900"}>
        {value}
      </span>
    </div>
  )
}

const optionsAsKeymap = (variantOptions: HttpTypes.StoreProductVariant["options"]) => {
  return variantOptions?.reduce((acc: Record<string, string>, varopt: any) => {
    acc[varopt.option_id] = varopt.value
    return acc
  }, {})
}

export default function ProductActions({
  product,
  disabled,
  paypalEnabled = false,
  customer,
}: ProductActionsProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [options, setOptions] = useState<Record<string, string | undefined>>({})
  const [isAdding, setIsAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sizeError, setSizeError] = useState<string | null>(null)
  const [floatingError, setFloatingError] = useTimedState<string | null>(null, 3000)
  const [customName, setCustomName] = useState(() => searchParams.get("edit_name") ?? "")
  const [customNumber, setCustomNumber] = useState(() => searchParams.get("edit_number") ?? "")
  const [selectedPatch, setSelectedPatch] = useState(() => searchParams.get("edit_patch") ?? "none")
  const [selectedCategory, setSelectedCategory] = useState<Category>("Mens")
  const [showPersonalisationPrompt, setShowPersonalisationPrompt] = useState(false)
  const countryCode = useParams().countryCode as string

  useEffect(() => {
    if (product.variants?.length === 1) {
      const variantOptions = optionsAsKeymap(product.variants[0].options)
      setOptions(variantOptions ?? {})
      return
    }
    // Preselect the size for a shared/edit-customisation link (?v_id=...) —
    // otherwise only the gallery image respects it (see page.tsx), leaving
    // the actual size unselected.
    const preselectedVariantId = searchParams.get("v_id")
    if (!preselectedVariantId) return
    const variant = product.variants?.find((v) => v.id === preselectedVariantId)
    if (variant) {
      setOptions(optionsAsKeymap(variant.options) ?? {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.variants])

  const selectedVariant = useMemo(() => {
    if (!product.variants || product.variants.length === 0) return
    return product.variants.find((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return isEqual(variantOptions, options)
    })
  }, [product.variants, options])

  const setOptionValue = (optionId: string, value: string) => {
    setOptions((prev) => ({ ...prev, [optionId]: value }))
    setSizeError(null)
  }

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    const value = !!selectedVariant ? selectedVariant?.id : null
    if (params.get("v_id") === value) return
    if (value) { params.set("v_id", value) } else { params.delete("v_id") }
    router.replace(pathname + "?" + params.toString())
  }, [selectedVariant])

  const inStock = useMemo(() => {
    if (!selectedVariant) return false
    return isVariantInStock(selectedVariant)
  }, [selectedVariant])

  const allOutOfStock = useMemo(() => {
    if (!product.variants || product.variants.length === 0) return true
    return product.variants.every((v) => !isVariantInStock(v))
  }, [product.variants])

  const outOfStockByOption = useMemo(() => {
    const map = new Map<string, Set<string>>()
    if (!product.options || !product.variants) return map
    for (const option of product.options) {
      const outOfStockValues = new Set<string>()
      for (const val of (option.values ?? []).map((v) => v.value)) {
        const variants = product.variants.filter((v) =>
          v.options?.some((o) => o.option_id === option.id && o.value === val)
        )
        if (variants.length > 0 && variants.every((v) => !isVariantInStock(v))) {
          outOfStockValues.add(val)
        }
      }
      map.set(option.id, outOfStockValues)
    }
    return map
  }, [product.options, product.variants])

  const categoriesWithSizes = useMemo(() => {
    const set = new Set<Category>()
    for (const cat of CATEGORIES) {
      const keywords = CATEGORY_SIZE_KEYWORDS[cat]
      const has = (product.options ?? []).some(
        (o) => o.title === "Size" && (o.values ?? []).some((v) => keywords.some((kw) => v.value.includes(kw)))
      )
      if (has) set.add(cat)
    }
    return set
  }, [product.options])

  // Falls back to showing all size values if none match the selected category (e.g. no baby sizes).
  const filteredProductOptions = useMemo(() => {
    const keywords = CATEGORY_SIZE_KEYWORDS[selectedCategory]
    return (product.options ?? []).map((option) => {
      if (option.title !== "Size" || !option.values) return option
      const filtered = option.values.filter((v) =>
        keywords.some((kw) => v.value.includes(kw))
      )
      return { ...option, values: filtered.length > 0 ? filtered : option.values }
    })
  }, [product.options, selectedCategory])

  // Reset size selection when switching category so a stale variant isn't carried over.
  useEffect(() => {
    if (product.variants?.length === 1) return
    setOptions({})
  }, [selectedCategory])

  const sizeSectionRef = useRef<HTMLDivElement>(null)
  const personalizationSectionRef = useRef<HTMLDivElement>(null)
  const scrollToPersonalisation = () =>
    personalizationSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    })

  // Arriving via the cart's "Edit customisation" link — scroll straight to the
  // personalisation form so the customer sees their existing name/number/patch
  // instead of landing on a blank product page.
  useEffect(() => {
    const isEditingCustomization =
      searchParams.get("edit_name") ||
      searchParams.get("edit_number") ||
      searchParams.get("edit_patch")
    if (isEditingCustomization) {
      scrollToPersonalisation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const actionsRef = useRef<HTMLDivElement>(null)
  const inView = useIntersection(actionsRef, "0px")

  const addToCartRef = useRef<HTMLDivElement>(null)
  const addToCartInView = useIntersection(addToCartRef, "0px")

  const [productSectionEnded, setProductSectionEnded] = useState(false)
  useEffect(() => {
    const el = document.getElementById("below-product-sections")
    const check = () => {
      if (!el) return
      const ended = el.getBoundingClientRect().top <= window.innerHeight
      setProductSectionEnded((prev) => (prev === ended ? prev : ended))
    }
    check()
    window.addEventListener("scroll", check, { passive: true })
    return () => window.removeEventListener("scroll", check)
  }, [])

  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => setIsMounted(true), [])

  const [floatingBarStyle, setFloatingBarStyle] = useState<{ left: number; width: number } | null>(null)
  useEffect(() => {
    const el = document.getElementById("product-right-col")
    if (!el) return
    const updatePosition = () => {
      const rect = el.getBoundingClientRect()
      setFloatingBarStyle((prev) =>
        prev?.left === rect.left && prev?.width === rect.width
          ? prev
          : { left: rect.left, width: rect.width }
      )
    }
    updatePosition()
    const observer = new ResizeObserver(updatePosition)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const hasPersonalisation = customName.length > 0 || customNumber.length > 0
  const patchPrice = CUSTOMIZATION_PATCHES.find((p) => p.id === selectedPatch)?.price ?? 0

  const editLineItemId = searchParams.get("edit_line_item")
  const editOriginalVariantId = searchParams.get("v_id")

  const addItemToCart = async (skipPersonalisation = false) => {
    if (!selectedVariant?.id) return
    setIsAdding(true)
    const result = await addToCartWithCustomization({
      variantId: selectedVariant.id,
      quantity: 1,
      countryCode,
      customName: skipPersonalisation ? "" : customName,
      customNumber: skipPersonalisation ? "" : customNumber,
      patchId: selectedPatch,
    })
    setIsAdding(false)
    if (!result.success) {
      setError(result.error || "Failed to add to cart. Please try again.")
    }
  }

  const handleAddToCart = async () => {
    setError(null)
    if (!selectedVariant?.id) {
      setSizeError("Please select a size before adding to cart.")
      sizeSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }
    setSizeError(null)

    if (editLineItemId) {
      if (editOriginalVariantId && selectedVariant.id !== editOriginalVariantId) {
        setError("Changing size isn't supported here — use \"Change\" on this item in your cart instead.")
        return
      }
      setIsAdding(true)
      const result = await updateCartItemCustomization({
        lineItemId: editLineItemId,
        customName,
        customNumber,
        patchId: selectedPatch,
      })
      setIsAdding(false)
      if (!result.success) {
        setError(result.error || "Failed to update customisation. Please try again.")
        return
      }
      router.push("/cart")
      return
    }

    // No name/number entered yet — confirm the customer really wants to skip
    // personalisation before adding, matching the reference site's flow.
    if (!hasPersonalisation) {
      setShowPersonalisationPrompt(true)
      return
    }

    await addItemToCart()
  }

  // "ADD PRINTING" — adds to cart using whatever name/number the customer
  // typed directly into the popup (may be blank, same as proceeding without).
  const handleAddPrintingFromModal = async () => {
    setShowPersonalisationPrompt(false)
    await addItemToCart()
  }

  // "NO THANKS, PLEASE PROCEED" — explicitly skips personalisation even if the
  // customer had typed something into the popup's fields.
  const handleProceedWithoutPersonalisation = async () => {
    setShowPersonalisationPrompt(false)
    setCustomName("")
    setCustomNumber("")
    await addItemToCart(true)
  }

  const isDisabled = allOutOfStock || (!!selectedVariant && !inStock) || !!disabled || isAdding
  const buttonLabel = allOutOfStock || (!!selectedVariant && !inStock)
    ? "Out of stock"
    : editLineItemId
      ? "Save changes"
      : "Add to cart now"

  const priceData = getProductPrice({ product, variantId: selectedVariant?.id })
  const currentPrice = selectedVariant ? priceData.variantPrice : priceData.cheapestPrice
  const personalizationPriceLabel = useMemo(
    () =>
      convertToLocale({
        amount: CUSTOMIZATION_PRICING.nameAndNumber,
        currency_code: priceData.cheapestPrice?.currency_code || "gbp",
      }),
    [priceData.cheapestPrice?.currency_code]
  )

  const content = (
    <>
      <div className="flex flex-col gap-y-6" ref={actionsRef}>
        {/* 1. Price */}
        <div>
          <ProductPrice product={product} variant={selectedVariant} />
        </div>

        {/* 2. Feature Highlights */}
        <ul className="space-y-2.5" aria-label="Product highlights">
          {PRODUCT_FEATURE_HIGHLIGHTS.map((highlight) => (
            <li key={highlight} className="flex items-center gap-3">
              <CircleCheck className="w-[22px] h-[22px] text-green-500 flex-shrink-0" aria-hidden="true" />
              <span className="text-[15px] text-gray-700">{highlight}</span>
            </li>
          ))}
        </ul>

        {/* 5. Category selector + size options */}
        {(product.variants?.length ?? 0) > 1 && (
          <div className="flex flex-col gap-y-5">
            {/* Category buttons — filter which sizes are shown */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">
                Personalise your shirt
              </p>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      disabled={!categoriesWithSizes.has(cat)}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        selectedCategory === cat
                          ? "bg-brand-navy text-white border-brand-navy"
                          : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      {cat}
                    </button>
                ))}
              </div>
            </div>

            {filteredProductOptions.map((option) => {
              const isSizeOption = option.title?.toLowerCase() === "size"
              return (
                <div key={option.id} ref={isSizeOption ? sizeSectionRef : undefined}>
                  <OptionSelect
                    option={option}
                    current={options[option.id]}
                    updateOption={setOptionValue}
                    title={option.title ?? ""}
                    data-testid="product-options"
                    disabled={!!disabled || isAdding}
                    outOfStockValues={outOfStockByOption.get(option.id)}
                  />
                  {isSizeOption && sizeError && (
                    <p className="text-red-500 text-sm mt-2">{sizeError}</p>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* 4. Personalisation form */}
        <div ref={personalizationSectionRef}>
          <ProductCustomization
            customName={customName}
            customNumber={customNumber}
            selectedPatch={selectedPatch}
            currencyCode={priceData.cheapestPrice?.currency_code || "gbp"}
            onNameChange={setCustomName}
            onNumberChange={setCustomNumber}
            onPatchChange={setSelectedPatch}
          />
        </div>

        {/* 5. Price Breakdown */}
        {currentPrice && (hasPersonalisation || patchPrice > 0) && (
          <div className="space-y-2 border-t border-gray-200 pt-4">
            <div className="flex items-baseline justify-between">
              <span className="text-base text-gray-700 italic">Shirt price:</span>
              <div className="flex items-baseline gap-2">
                {currentPrice.price_type === "sale" && (
                  <span className="text-base text-red-400 line-through">{currentPrice.original_price}</span>
                )}
                <span className="text-base font-semibold text-gray-900">{currentPrice.calculated_price}</span>
              </div>
            </div>
            {hasPersonalisation && (
              <PriceBreakdownRow
                label="Personalisation:"
                value={convertToLocale({ amount: CUSTOMIZATION_PRICING.nameAndNumber, currency_code: currentPrice.currency_code })}
              />
            )}
            {patchPrice > 0 && (
              <PriceBreakdownRow
                label="Patch:"
                value={convertToLocale({ amount: patchPrice, currency_code: currentPrice.currency_code })}
              />
            )}
            <PriceBreakdownRow
              label="Total price:"
              value={convertToLocale({
                amount: currentPrice.calculated_price_number + (hasPersonalisation ? CUSTOMIZATION_PRICING.nameAndNumber : 0) + patchPrice,
                currency_code: currentPrice.currency_code,
              })}
              bold
            />
          </div>
        )}

        {/* 7. Add to Cart + PayPal Express + Wishlist */}
        <div ref={addToCartRef} className="flex gap-3 items-start">
          {/* Stacked column so both buttons share the same left/right edges */}
          <div className="flex-1 flex flex-col gap-2">
            {allOutOfStock || (!!selectedVariant && !inStock) ? (
              <NotifyMeButton variantId={selectedVariant?.id} isLoggedIn={!!customer} />
            ) : (
              <>
                <button
                  onClick={handleAddToCart}
                  disabled={isDisabled}
                  className="w-full h-14 rounded-lg bg-brand-hero-navy text-white text-lg font-bold hover:bg-brand-navy disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  data-testid="add-product-button"
                >
                  {isAdding && <Loader2 className="w-5 h-5 animate-spin" />}
                  {buttonLabel}
                </button>
                {paypalEnabled && (
                  <PaypalExpressButton
                    variantId={selectedVariant?.id ?? null}
                    quantity={1}
                    countryCode={countryCode}
                    height={55}
                    onError={(msg) => setError(msg)}
                  />
                )}
              </>
            )}
          </div>
          <WishlistButton
            productId={product.id}
            productTitle={product.title ?? ""}
            className="w-14 h-14 rounded-lg border border-gray-300 flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-300 transition-colors flex-shrink-0"
          />
        </div>
        {error && <p className="text-red-500 text-sm">{error}</p>}

        {/* 7b. Accepted payment methods */}
        <PaymentIcons />

        {/* 8. Shipping Method */}
        <ShippingMethodWidget />

        <MobileActions
          product={product}
          variant={selectedVariant}
          options={options}
          updateOptions={setOptionValue}
          inStock={inStock}
          allOutOfStock={allOutOfStock}
          handleAddToCart={handleAddToCart}
          isAdding={isAdding}
          show={!addToCartInView}
          optionsDisabled={!!disabled || isAdding}
        />
      </div>

      <Modal
        isOpen={showPersonalisationPrompt}
        close={() => setShowPersonalisationPrompt(false)}
        panelClassName="max-w-3xl"
        data-testid="personalization-prompt-modal"
      >
        <div className="relative flex flex-col small:flex-row">
          <button
            type="button"
            onClick={() => setShowPersonalisationPrompt(false)}
            aria-label="Close"
            data-testid="personalization-close-button"
            className="absolute right-4 top-4 z-10 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
          >
            <X size={20} />
          </button>

          {/* Product preview */}
          <ProductThumbnail
            src={product.thumbnail}
            alt={product.title ?? ""}
            sizes="(min-width: 1024px) 300px, 100vw"
            imageClassName="object-contain p-6"
            containerClassName="w-full small:w-2/5 aspect-square small:aspect-auto flex-shrink-0"
          />

          {/* Personalisation form */}
          <div className="flex-1 p-6 small:p-8 flex flex-col gap-y-5">
            <div>
              <Dialog.Title
                as="h2"
                className="text-[22px] small:text-[26px] font-medium leading-tight text-brand-navy pr-8"
              >
                {PERSONALIZATION_PROMPT_LABELS.heading}
              </Dialog.Title>
              <p className="text-base text-gray-500 mt-2">
                {PERSONALIZATION_PROMPT_LABELS.subheading}
              </p>
            </div>

            <PersonalizationModalField
              id="personalization-modal-name"
              label={PERSONALIZATION_PROMPT_LABELS.nameLabel}
              value={customName}
              onChange={setCustomName}
              maxLength={CUSTOMIZATION_LIMITS.nameMaxLength}
              testId="personalization-modal-name-input"
            />

            <PersonalizationModalField
              id="personalization-modal-number"
              label={PERSONALIZATION_PROMPT_LABELS.numberLabel}
              value={customNumber}
              onChange={setCustomNumber}
              maxLength={CUSTOMIZATION_LIMITS.numberMaxLength}
              numeric
              testId="personalization-modal-number-input"
            />

            <p className="text-xs text-gray-500">
              {PERSONALIZATION_PROMPT_LABELS.summaryLabel} ( +{personalizationPriceLabel} )
            </p>

            <div className="flex flex-col gap-y-3">
              <button
                type="button"
                onClick={handleAddPrintingFromModal}
                disabled={isAdding}
                data-testid="personalization-add-printing-button"
                className="w-full py-[13px] px-6 rounded-full bg-green-600 border-2 border-green-600 text-white text-[15px] font-bold uppercase hover:bg-green-700 hover:border-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isAdding && <Loader2 className="w-4 h-4 animate-spin" />}
                {PERSONALIZATION_PROMPT_LABELS.addPrintingLabel}
              </button>
              <button
                type="button"
                onClick={handleProceedWithoutPersonalisation}
                disabled={isAdding}
                data-testid="personalization-no-thanks-button"
                className="w-full py-[13px] px-6 rounded-full bg-transparent border-2 border-gray-300 text-gray-500 text-[15px] font-bold uppercase hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isAdding && <Loader2 className="w-4 h-4 animate-spin" />}
                {PERSONALIZATION_PROMPT_LABELS.noThanksLabel}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Floating Add to Cart bar — desktop only.
          Always mounted so CSS transition can animate slide-in / slide-out.
          Visible when add-to-cart button has scrolled out of view AND the
          below-product sections haven't started entering the viewport yet. */}
      {isMounted && createPortal(
        <div
          className="hidden small:block fixed bottom-0 z-floating-bar bg-white"
          style={{
            ...(floatingBarStyle ?? {}),
            transform: (!addToCartInView && !productSectionEnded)
              ? "translateY(0)"
              : "translateY(110%)",
            transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {floatingError && (
            <div className="px-4 pt-2 pb-0 w-full">
              <p className="text-sm text-red-500 font-medium text-center">{floatingError}</p>
            </div>
          )}
          <div className="px-4 py-3 flex items-center gap-3 w-full">
            <button
              onClick={handleAddToCart}
              disabled={isDisabled}
              className="flex-1 h-11 rounded-lg bg-brand-hero-navy text-white font-bold hover:bg-brand-navy disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isAdding && <Loader2 className="w-4 h-4 animate-spin" />}
              {buttonLabel}
            </button>
            {paypalEnabled && (
              <div className="flex-1">
                <PaypalExpressButton
                  variantId={selectedVariant?.id ?? null}
                  quantity={1}
                  countryCode={countryCode}
                  height={44}
                  onError={(msg) => setFloatingError(msg)}
                />
              </div>
            )}
            <WishlistButton
              productId={product.id}
              productTitle={product.title ?? ""}
              className="w-11 h-11 rounded-lg border border-gray-300 flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-300 transition-colors flex-shrink-0"
            />
          </div>
        </div>,
        document.body
      )}
    </>
  )

  return paypalEnabled ? (
    <PaypalScriptProviderWrapper>{content}</PaypalScriptProviderWrapper>
  ) : (
    content
  )
}
